import React, { useState, useEffect } from "react";
import { Download, ArrowRight, Check, Mic2, MapPin, Calendar, Quote } from "lucide-react";
import { speaking } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { createSpeakerInquiry, getEvents, downloadUrl } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Speaking() {
  const { toast } = useToast();
  const [form, setForm] = useState({ org: "", contact: "", email: "", type: "", date: "", size: "", goal: "" });
  const [events, setEvents] = useState([]);

  useEffect(() => {
    getEvents().then((data) => { if (Array.isArray(data)) setEvents(data); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createSpeakerInquiry(form);
      toast({ title: "Booking inquiry submitted!", description: "We'll review and respond within 2 business days." });
      setForm({ org: "", contact: "", email: "", type: "", date: "", size: "", goal: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-navy text-cream overflow-hidden">
        <img src={speaking.hero.image} alt="Keynote" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/50" />
        <div className="relative max-w-4xl mx-auto px-5 py-24">
          <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase flex items-center gap-2">
            <Mic2 size={16} /> Corporate Seminars, Keynotes &amp; Leadership Workshops
          </p>
          <h1 className="font-serif-display text-5xl md:text-7xl font-bold mt-4 leading-[0.95]">{speaking.hero.headline}</h1>
          <p className="font-ui text-white/85 mt-6 text-xl leading-relaxed max-w-2xl">{speaking.hero.subhead}</p>
          <div className="flex flex-wrap gap-4 mt-9">
            <a href="#inquiry" className="inline-flex items-center gap-2 bg-teal text-navy px-7 py-4 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
              {speaking.hero.primaryCta} <ArrowRight size={18} />
            </a>
            <a href={downloadUrl("speaker-one-sheet")} target="_blank" rel="noreferrer" onClick={() => toast({ title: "Downloading one-sheet…", description: "Your BOSSY speaker one-sheet PDF is on its way." })} className="inline-flex items-center gap-2 border border-white/40 px-7 py-4 rounded-full font-ui font-semibold hover:bg-white/10 transition-colors">
              <Download size={18} /> {speaking.hero.secondaryCta}
            </a>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="max-w-4xl mx-auto px-5 py-16 text-center">
        <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase">The BOSSY Approach</p>
        <p className="font-serif-body text-2xl md:text-3xl text-navy mt-5 leading-relaxed">{speaking.approach}</p>
      </section>

      {/* Hosts */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-serif-display text-4xl font-bold text-navy text-center">Two Perspectives, One Unified System</h2>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {speaking.hosts.map((h) => (
              <div key={h.name} className="bg-cream rounded-2xl overflow-hidden border flex" style={{ borderColor: "var(--cream-dark)" }}>
                <img src={h.img} alt={h.name} className="w-32 md:w-40 object-cover shrink-0" />
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl font-bold text-navy">{h.name}</h3>
                  <p className="font-ui text-navy/70 text-sm mt-3 leading-relaxed">{h.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-serif-display text-4xl font-bold text-navy text-center">Workshop Modules</h2>
        <p className="font-ui text-navy/60 text-center mt-3 max-w-2xl mx-auto">Half-day (3-hour) and full-day interactive modules, tailored for executive teams, corporate departments, or industry associations.</p>
        <div className="grid md:grid-cols-3 gap-7 mt-12">
          {speaking.modules.map((m) => (
            <div key={m.code} className="lift bg-white rounded-2xl p-7 border flex flex-col" style={{ borderColor: "var(--cream-dark)" }}>
              <span className="font-ui text-xs font-bold text-teal uppercase tracking-wider">{m.code}</span>
              <h3 className="font-serif-display text-2xl font-semibold text-navy mt-2 leading-tight">{m.title}</h3>
              <p className="font-ui text-sm text-orange italic mt-2">{m.focus}</p>
              <ul className="mt-5 space-y-3 flex-1">
                {m.learn.map((l) => (
                  <li key={l} className="flex gap-2.5 font-ui text-sm text-navy/80">
                    <Check size={17} className="text-teal shrink-0 mt-0.5" /> {l}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Keynotes */}
      <section className="bg-navy text-cream py-20">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-serif-display text-4xl font-bold text-center">Keynote Topics</h2>
          <p className="font-ui text-white/60 text-center mt-3">45–60 minute sessions for annual meetings, conventions, and corporate summits.</p>
          <div className="space-y-5 mt-12">
            {speaking.keynotes.map((k, i) => (
              <div key={k.title} className="flex gap-5 items-start bg-white/5 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                <span className="font-serif-display text-4xl font-bold text-teal/60 shrink-0">0{i + 1}</span>
                <div>
                  <h3 className="font-serif-display text-2xl font-semibold">"{k.title}"</h3>
                  <p className="font-ui text-white/75 mt-2 leading-relaxed">{k.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fast facts table */}
      <section className="max-w-5xl mx-auto px-5 py-20">
        <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-10">Event Organizer Fast-Facts</h2>
        <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--cream-dark)" }}>
          <table className="w-full bg-white">
            <thead>
              <tr className="bg-navy text-cream font-ui text-sm">
                <th className="text-left px-6 py-4 font-semibold">Format</th>
                <th className="text-left px-6 py-4 font-semibold">Duration</th>
                <th className="text-left px-6 py-4 font-semibold">Audience Size</th>
                <th className="text-left px-6 py-4 font-semibold">Best For</th>
              </tr>
            </thead>
            <tbody>
              {speaking.facts.map((f, i) => (
                <tr key={f.format} className={i % 2 ? "bg-cream/50" : ""} style={{ borderTop: "1px solid var(--cream-dark)" }}>
                  <td className="px-6 py-4 font-serif-display font-semibold text-navy">{f.format}</td>
                  <td className="px-6 py-4 font-ui text-sm text-navy/75">{f.duration}</td>
                  <td className="px-6 py-4 font-ui text-sm text-navy/75">{f.audience}</td>
                  <td className="px-6 py-4 font-ui text-sm text-navy/75">{f.best}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Event Recaps */}
      {events.length > 0 && (
        <section className="bg-cream-dark/40 py-20">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase">Recent Events</p>
              <h2 className="font-serif-display text-4xl font-bold text-navy mt-3">Where we've been</h2>
              <p className="font-ui text-navy/60 mt-3">Recaps from recent keynotes, workshops, and retreats — with the takeaways attendees actually kept.</p>
            </div>
            <div className="space-y-10">
              {events.map((ev, i) => (
                <div key={ev.id} className={`grid md:grid-cols-2 gap-8 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                  <div className="md:[direction:ltr] rounded-2xl overflow-hidden lift">
                    <img src={ev.img} alt={ev.title} className="w-full h-64 object-cover" />
                  </div>
                  <div className="md:[direction:ltr]">
                    <div className="flex flex-wrap items-center gap-4 font-ui text-sm text-navy/55 mb-3">
                      <span className="flex items-center gap-1.5"><Calendar size={15} className="text-teal-dark" /> {ev.date}</span>
                      <a href={ev.venueUrl} className="flex items-center gap-1.5 hover:text-orange transition-colors"><MapPin size={15} className="text-teal-dark" /> {ev.venue}</a>
                    </div>
                    <h3 className="font-serif-display text-3xl font-bold text-navy leading-tight">{ev.title}</h3>
                    <p className="font-ui text-navy/75 mt-3 leading-relaxed">{ev.summary}</p>
                    <ul className="mt-4 space-y-2">
                      {ev.takeaways.map((t) => (
                        <li key={t} className="flex gap-2.5 font-ui text-sm text-navy/80"><Check size={17} className="text-teal-dark shrink-0 mt-0.5" /> {t}</li>
                      ))}
                    </ul>
                    <div className="mt-5 bg-white rounded-xl p-5 border-l-4" style={{ borderColor: "var(--teal)" }}>
                      <Quote size={20} className="text-teal-dark mb-1" />
                      <p className="font-serif-body text-lg text-navy italic leading-snug">"{ev.quote}"</p>
                      <p className="font-ui text-xs text-navy/50 mt-2">— {ev.quoteBy}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Inquiry form */}
      <section id="inquiry" className="bg-white py-20">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-serif-display text-4xl font-bold text-navy text-center">Speaker Inquiry</h2>
          <p className="font-ui text-navy/60 text-center mt-3">Tell us about your event and we'll get right back to you.</p>
          <form onSubmit={submit} className="mt-10 space-y-4 bg-cream rounded-2xl p-8 border" style={{ borderColor: "var(--cream-dark)" }}>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Organization / Company" className="px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal bg-white" style={{ borderColor: "var(--cream-dark)" }} />
              <input required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Contact name & title" className="px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal bg-white" style={{ borderColor: "var(--cream-dark)" }} />
            </div>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email & phone" className="w-full px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal bg-white" style={{ borderColor: "var(--cream-dark)" }} />
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="bg-white h-12 font-ui text-sm"><SelectValue placeholder="Event type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="keynote">Keynote Speech</SelectItem>
                <SelectItem value="half-day">Half-Day Workshop</SelectItem>
                <SelectItem value="full-day">Full-Day Retreat</SelectItem>
                <SelectItem value="panel">Conference Panel</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="Proposed date(s) & location" className="px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal bg-white" style={{ borderColor: "var(--cream-dark)" }} />
              <input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="Estimated audience size" className="px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal bg-white" style={{ borderColor: "var(--cream-dark)" }} />
            </div>
            <textarea value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Primary goal / desired outcome for the session" rows={3} className="w-full px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal resize-none bg-white" style={{ borderColor: "var(--cream-dark)" }} />
            <button type="submit" className="w-full py-4 rounded-full bg-navy text-cream font-ui font-semibold hover:bg-orange transition-colors flex items-center justify-center gap-2">
              Submit Booking Inquiry <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
